import {createContext, ReactNode, useContext} from 'react'
import {useSoftwareQualityOverview} from './useSoftwareQualityOverview'
import {AssociatedPoFTopic, SoftwareEvaluationDataForReporting} from './apiOrganisationSoftwareQuality'
import {DownloadFormat, SavedQualityReport} from '~/components/organisation/reporting/apiOrganisationSoftwareQuality'
import {PoFTopicNode} from '~/components/software/indicator/apiSoftwareIndicator'

type SoftwareQualityOverviewContextType = {
  organisationId?: string
  year?: string
  evaluatedSoftware: SoftwareEvaluationDataForReporting[]
  savedQualityReports: SavedQualityReport[]
  downloadQualityReports: (format: DownloadFormat) => void,
  downloadSavedQualityReports: (id: number, format: DownloadFormat) => void,
  pofTopicTrees: PoFTopicNode[]
  savePoFTopicForOrganisation: (topic_id: string) => void,
  deletePoFTopicForOrganisation: (topic_id: string) => void,
  associatedPofTopics: AssociatedPoFTopic[]
}

const SoftwareQualityOverviewContext = createContext<SoftwareQualityOverviewContextType>({
  organisationId: '',
  year: '',
  evaluatedSoftware: [],
  pofTopicTrees: [],
  savedQualityReports: [],
  downloadQualityReports: (format: DownloadFormat) => {},
  downloadSavedQualityReports: (id: number, format: DownloadFormat) => {},
  savePoFTopicForOrganisation: (topic_id: string) => {},
  deletePoFTopicForOrganisation: (topic_id: string) => {},
  associatedPofTopics: []
})

type SoftwareQualityOverviewProviderProps = {
  organisationId?: string
  year?: string
  children: ReactNode
}

export function SoftwareQualityOverviewProvider({organisationId, year, children}: SoftwareQualityOverviewProviderProps) {
  const {
    evaluatedSoftware,
    savedQualityReports,
    downloadQualityReports,
    downloadSavedQualityReports,
    pofTopicTrees,
    savePoFTopicForOrganisation,
    deletePoFTopicForOrganisation,
    associatedPofTopics
  } = useSoftwareQualityOverview(organisationId, year)

  return (
    <SoftwareQualityOverviewContext.Provider value={
      {
        organisationId,
        year,
        evaluatedSoftware,
        savedQualityReports,
        downloadQualityReports,
        downloadSavedQualityReports,
        pofTopicTrees,
        savePoFTopicForOrganisation,
        deletePoFTopicForOrganisation,
        associatedPofTopics
      }
    }
    >
      {children}
    </SoftwareQualityOverviewContext.Provider>
  )
}

export function useSoftwareQualityOverviewContext() {
  const ctx = useContext(SoftwareQualityOverviewContext)
  if (!ctx) {
    throw new Error('useSoftwareQualityOverviewContext must be inside SoftwareQualityOverviewProvider')
  }
  return ctx
}
